import { cn } from './cn';

/**
 * Shimmer skeleton primitive + a few presets. Replaces the mix of bare `<Spin>`,
 * `animate-pulse` blocks and "Loading…" text the audit found across the app. Use a
 * skeleton that MIRRORS the eventual layout so content doesn't jump when it lands.
 *
 * The shimmer sweep comes from the `--animate-shimmer` token (theme.css) and is
 * neutralized by the global reduced-motion guard.
 */
const SHIMMER = 'bg-[linear-gradient(90deg,var(--gb-surface-2)_25%,var(--gb-surface-3)_37%,var(--gb-surface-2)_63%)] bg-[length:200%_100%] animate-shimmer';

export interface SkeletonProps {
  className?: string;
  /** Fully rounded (for avatars/dots). */
  circle?: boolean;
  // ── antd Skeleton compatibility ──
  active?: boolean;
  loading?: boolean;
  round?: boolean;
  title?: boolean | { width?: number | string };
  paragraph?: boolean | { rows?: number; width?: number | string | (number | string)[] };
  avatar?: boolean | { size?: number | 'small' | 'large' | 'default'; shape?: 'circle' | 'square' };
  children?: import('react').ReactNode;
}

/** Shimmer skeleton. Simple block by default (className/circle); renders an antd-style
 *  title+paragraph(+avatar) layout when the antd props (paragraph/title/avatar) are used. */
export function Skeleton({ className, circle, loading, title, paragraph, avatar, children }: SkeletonProps) {
  if (loading === false && children != null) return <>{children}</>;

  const antdMode = title !== undefined || paragraph !== undefined || avatar !== undefined;
  if (!antdMode) {
    return <span aria-hidden="true" className={cn('block', SHIMMER, circle ? 'rounded-full' : 'rounded-control', className)} />;
  }

  const rows = paragraph === false ? 0 : typeof paragraph === 'object' ? (paragraph.rows ?? 3) : 3;
  return (
    <div aria-hidden="true" className={cn('flex gap-3', className)}>
      {avatar && <span className={cn('size-10 shrink-0', SHIMMER, typeof avatar === 'object' && avatar.shape === 'square' ? 'rounded-lg' : 'rounded-full')} />}
      <div className="flex-1 space-y-2.5">
        {title !== false && <span className={cn('block h-4 w-1/3 rounded', SHIMMER)} />}
        {Array.from({ length: rows }).map((_, i) => (
          <span key={i} className={cn('block h-3.5 rounded', SHIMMER, i === rows - 1 ? 'w-2/3' : 'w-full')} />
        ))}
      </div>
    </div>
  );
}

/** N lines of text skeleton; the last line is shortened for a natural look. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn('h-3.5', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

/** A grid of KPI-card skeletons — for dashboards / list-page stat rows. */
export function SkeletonStats({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-card border border-line bg-surface p-4 shadow-soft">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="mt-3 h-7 w-32" />
          <Skeleton className="mt-2 h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

/** A table skeleton (header + rows) that reserves the table's footprint. */
export function SkeletonTable({ rows = 6, cols = 5, className }: { rows?: number; cols?: number; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-card border border-line bg-surface', className)}>
      <div className="flex gap-4 border-b border-line bg-surface-2 px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 border-b border-line/60 px-4 py-3.5 last:border-0">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={cn('h-4 flex-1', c === 0 && 'max-w-[40%]')} />
          ))}
        </div>
      ))}
    </div>
  );
}
